// Everything an embedded widget needs that is not its frame.
//
// An embedded widget is a frame the host puts inline in a page it already
// renders: a `container`, or a `tab` in a tab bar the page draws. The frame itself, and the platform behaviour
// that comes with it (the `apps/widget_*` analytics with `widgetAttribution`, the
// Sentry signals, the session and its recovery, the effects a widget asks for,
// the error state and Retry), is `SlotContainer`'s, exactly as for a full-page
// widget. The draft channel (`?lumi_draft`) reaches the assignments through
// `useSlotAssignments`, and every URL after that is the platform's, built for the
// same channel. What is left for this hook is which widgets get a frame at all:
// the kind, the params, and the one-app rule of a container.
// `AppContainerSlot` is the standard presentation.

import { resolveLocalizedName, type SlotId, SLOTS, unmetParamNames, type WidgetKind } from '@luminovo/apps-types';
import { useEffect, useMemo, useState } from 'react';

import { type AddressableSlotAssignment, isAddressable } from '../api/fetchSlotAssignments';
import { useSlotErrorReporting } from '../errors/slotErrorReporting';
import { compareWidgetKeys } from '../routing/matchFullPageAssignment';
import { useAppsSession } from './useAppsSession';
import { useSlotAssignments } from './useSlotAssignments';

// The kinds a page embeds inline, as opposed to a `full-page` widget, which owns
// a route, and a `button`, which the host draws. Their frame sits in the page's
// own content, so its height and its error row follow from this list too
// (`useFrameHeight`, `SlotContainer`): a new inline kind is added here only.
export const EMBEDDED_WIDGET_KINDS: ReadonlySet<WidgetKind> = new Set(['container', 'tab']);

// The kinds whose slot shows one app. Two containers in one sidebar slot would
// stack two frames of unknown height in a column sized for one, and the upload
// cannot refuse the second, since every app is its own document. So the host
// draws the usable widget with the lowest `${app.id}:${widget.id}`, the same
// tiebreak as two full-page widgets on one route, and reports the others to
// Luminovo's error monitoring (the host's `SlotErrorReportingProvider`, Sentry
// in the product). Their authors are not told: nothing reaches them yet.
const ONE_PER_SLOT_KINDS: ReadonlySet<WidgetKind> = new Set(['container']);

/** One embedded widget this slot shows, ready to hand to `SlotContainer`. */
export interface SlotEmbed {
    /** `${app.id}:${widget.id}`. Stable across renders; unique within the slot. */
    key: string;
    assignment: AddressableSlotAssignment;
    /** The apps host the frame loads from and the only origin it trusts. */
    appsOrigin: string;
    /** The app-authored widget name, resolved for the locale. */
    name: string;
}

const assignmentKey = (assignment: AddressableSlotAssignment): string => `${assignment.app.id}:${assignment.widget.id}`;

/**
 * The embedded widgets a slot shows, ordered by `${app.id}:${widget.id}`. A
 * container slot returns at most one: the lowest key.
 *
 * A widget requiring a param the slot does not supply, or has no value for, is
 * withheld; a container that loses to the winner is dropped. Each is reported
 * once through `useSlotErrorReporting`, which goes to Luminovo's error monitoring,
 * not to the app's author. Which widget wins never depends on how a frame fared:
 * a widget that fails keeps the slot and shows its error row, so a failure, of
 * the widget or of the session, never hands the slot to another app.
 *
 * Empty while the apps session is being minted, so a page does not draw a frame
 * and then have to explain it; a refused session still returns the widget, whose
 * frame shows the error. Empty when nothing is usable, so a page with no app
 * looks the way it did before.
 */
export function useSlotEmbeds(
    slotId: SlotId,
    params: Readonly<Record<string, unknown>>,
    locale: string,
): readonly SlotEmbed[] {
    return useSlotEmbedsState(slotId, params, locale).embeds;
}

/** What `useSlotEmbeds` returns, plus whether it is the final answer. */
export interface SlotEmbedsState {
    embeds: readonly SlotEmbed[];
    /**
     * The assignments have arrived and, when something is usable, the apps session
     * has been minted or refused. Until then an empty `embeds` means "not yet", not
     * "nothing here".
     */
    settled: boolean;
    /** The assignments request failed, so an empty `embeds` is not the tenant's answer. */
    degraded: boolean;
}

/**
 * `useSlotEmbeds` for a page that must tell "no widget" from "not known yet",
 * such as a route that names one widget and has to say when it is gone.
 */
export function useSlotEmbedsState(
    slotId: SlotId,
    params: Readonly<Record<string, unknown>>,
    locale: string,
): SlotEmbedsState {
    const { data, isPlaceholderData } = useSlotAssignments(slotId);
    const reportError = useSlotErrorReporting();
    const [reported] = useState(() => new Set<string>());
    const appsOrigin = data?.appsOrigin;
    const slotKind = SLOTS[slotId].kind;

    const { usable, misconfigured } = useMemo(() => {
        const candidates: Array<Omit<SlotEmbed, 'appsOrigin'>> = [];
        const skipped: Array<{ key: string; reason: string }> = [];

        if (!EMBEDDED_WIDGET_KINDS.has(slotKind)) return { usable: [], misconfigured: [] };

        for (const assignment of data?.assignments ?? []) {
            if (assignment.widget.kind !== slotKind || !isAddressable(assignment)) continue;
            const key = assignmentKey(assignment);
            // The same rule the upload refuses by, for a document stored before
            // the slot changed what it supplies.
            const unmet = unmetParamNames(slotId, assignment.widget.paramsSchema);
            if (unmet.length > 0) {
                skipped.push({ key, reason: `needs params ${slotId} does not supply: ${unmet.join(', ')}` });
                continue;
            }
            // The slot supplies the name, but the page has no value for it yet.
            const missing = (assignment.widget.paramsSchema.required ?? []).filter(
                (name) => params[name] === undefined,
            );
            if (missing.length > 0) {
                skipped.push({ key, reason: `has no value for ${missing.join(', ')} in ${slotId}` });
                continue;
            }
            candidates.push({ key, assignment, name: resolveLocalizedName(assignment.widget.name, locale) });
        }

        // The assignments API promises no ordering, so the host imposes one. For a
        // container it also decides which app the slot shows.
        candidates.sort((a, b) => compareWidgetKeys(a.key, b.key));
        if (!ONE_PER_SLOT_KINDS.has(slotKind) || candidates.length <= 1) {
            return { usable: candidates, misconfigured: skipped };
        }
        const [winner, ...dropped] = candidates;
        for (const { key } of dropped) {
            skipped.push({ key, reason: `was not drawn: ${slotId} shows one app, and ${winner!.key} comes first` });
        }
        return { usable: [winner!], misconfigured: skipped };
    }, [data, locale, params, slotId, slotKind]);

    useEffect(() => {
        for (const { key, reason } of misconfigured) {
            if (reported.has(key)) continue;
            reported.add(key);
            reportError(new Error(`Widget ${key} ${reason}`), { level: 'warning', tags: { slotId } });
        }
    }, [misconfigured, reportError, reported, slotId]);

    // Skipped entirely while nothing is usable, so a page nobody has extended
    // costs no session request. `SlotContainer` reads the same cached session.
    const appsSession = useAppsSession(usable.length > 0 ? appsOrigin : undefined);
    const sessionSettled = appsSession.data !== undefined || appsSession.isError;
    // Placeholder data is the previous channel's answer, kept on screen while this one loads, so it settles nothing.
    const settled = data !== undefined && !isPlaceholderData && (usable.length === 0 || sessionSettled);
    const degraded = data?.degraded ?? false;

    return useMemo(
        () => ({
            embeds:
                appsOrigin === undefined || !sessionSettled ? [] : usable.map((embed) => ({ ...embed, appsOrigin })),
            settled,
            degraded,
        }),
        [appsOrigin, degraded, sessionSettled, settled, usable],
    );
}
